/**
 * @fileoverview Maps registered GA4 events to session engagement signals.
 * @description When `sendEvent` fires a known engagement event, we increment
 * session counters automatically — deepening insight without migrating every
 * call site to `trackSiteAction`.
 *
 * @module lib/site-intelligence/engagement-signals
 */

/** Weight + intent flag for automatic engagement fan-out. */
export type EngagementSignalSpec = Readonly<{
  weight: number
  highIntent?: boolean
  /** When false, only `signal_weight` increments (not `action_count`). */
  countsAsAction?: boolean
}>

/** High-intent signal for funnel entry — recorded synchronously in request-access-analytics. */
export const REQUEST_ACCESS_START_SIGNAL: EngagementSignalSpec = {
  weight: 4,
  highIntent: true,
}

/**
 * Keys must match GA4 event names in `EVENT_REGISTRY`.
 */
export const ENGAGEMENT_SIGNAL_BY_EVENT: Readonly<Record<string, EngagementSignalSpec>> = {
  scroll_depth: { weight: 1, countsAsAction: false },
  button_click: { weight: 3, highIntent: true },
  resource_view: { weight: 1 },
  resource_interact: { weight: 2 },
  resource_complete: { weight: 3 },
  resource_download: { weight: 3, highIntent: true },
  search: { weight: 2 },
  search_result_click: { weight: 2 },
  filter_content: { weight: 1 },
  email_capture: { weight: 3, highIntent: true },
  faq_interact: { weight: 1 },
  video_play: { weight: 2 },
  video_complete: { weight: 2 },
  begin_checkout: { weight: 5, highIntent: true },
  view_item: { weight: 4, highIntent: true },
  // request_access_start is recorded synchronously in request-access-analytics
  // so conversion intelligence is correct when gtag is still queued.
}

/**
 * Returns the engagement signal spec for an event, if any.
 *
 * @param {string} eventName - GA4 event name.
 * @returns {EngagementSignalSpec | undefined} Signal spec or undefined.
 */
export function engagementSignalFor(eventName: string): EngagementSignalSpec | undefined {
  return ENGAGEMENT_SIGNAL_BY_EVENT[eventName]
}
